/**
 * Always-on hover/focus tooltip for controls that carry no visible label
 * (icon-only buttons, rail items, etc.). Unlike OverflowTooltip — which only
 * surfaces when truncated text overflows — this fires on every hover/focus.
 *
 * Attaches its handlers to the single child via cloneElement instead of
 * wrapping it, so it adds no DOM node: the trigger stays a direct flex/grid
 * child and any layout CSS keyed off it (e.g. the rail's [data-origin-mode]
 * height-collapse) keeps working. The popover itself portals to <body> and
 * reuses the same treatment as the rest of the dashboard's tooltips.
 *
 * Use `aria-label` on the child for the accessible name; `label` here is the
 * visible text. Prefer this over the native `title` attribute (≈1s delay,
 * single-line, platform-styled).
 */
import { cloneElement, useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TOOLTIP_LAYER } from "../lib/tooltipLayer.js";

// `wide` switches from the one-line label treatment to a wrapping
// card (max-width ~300px) for explainer content — `label` can be a
// React node in that mode. `maxWidth` (a NUMBER of px, `wide` only)
// widens that card for content that needs reading room — e.g. a trace
// turn shown behind a citation. Kept a number, not a style object: this
// component is on the render path, so the escape hatch must not be able
// to carry a URL-capable value.
// `anchorRect` drives the tooltip from a virtual trigger instead of a child
// element: a canvas plane has no per-mark DOM to hang a tooltip on, but it
// still wants this treatment and, more importantly, this edge correction. Pass
// a client-space rect and omit `children`; the popover shows while the rect is
// non-null and hides when it goes null.
export default function HoverTooltip({ label, placement = "right", wide = false, maxWidth = null, children = null, anchorRect = null }) {
    const widePx = wide && typeof maxWidth === "number" && maxWidth > 0 ? maxWidth : null;
    const ref = useRef(null);
    const popRef = useRef(null);
    const correctedRef = useRef(false);
    const [pos, setPos] = useState(null); // { top, left } | null

    const anchorRef = useRef(anchorRect);
    anchorRef.current = anchorRect;
    const triggerRect = useCallback(
        () => anchorRef.current || ref.current?.getBoundingClientRect() || null,
        [],
    );

    const show = useCallback(() => {
        const r = triggerRect();
        if (!r || !label) return;
        correctedRef.current = false;
        const vw = window.innerWidth;
        if (placement === "right") {
            // Right of the trigger, vertically centered (left rail).
            setPos({ top: `${r.top + r.height / 2}px`, left: `${r.right + 8}px`, transform: "translateY(-50%)" });
        } else if (placement === "left") {
            // Left of the trigger, vertically centered (right-edge rail).
            // Anchored by its right edge so it grows leftward, away from
            // the viewport edge the rail hugs.
            setPos({ top: `${r.top + r.height / 2}px`, right: `${vw - r.left + 8}px`, transform: "translateY(-50%)" });
        } else if (placement === "top") {
            // Above the trigger. Anchor to whichever edge keeps a wide
            // tooltip on-screen: right-half controls grow leftward (anchor
            // the tooltip's right edge), left-half controls grow rightward.
            const top = `${r.top - 6}px`;
            setPos((r.left + r.right) / 2 > vw / 2
                ? { top, right: `${vw - r.right}px`, transform: "translateY(-100%)" }
                : { top, left: `${r.left}px`, transform: "translateY(-100%)" });
        } else {
            // Below-left (default).
            setPos({ top: `${r.bottom + 4}px`, left: `${r.left}px` });
        }
    }, [label, placement, triggerRect]);

    const hide = useCallback(() => { correctedRef.current = false; setPos(null); }, []);

    // Edge-correction: after the popover mounts at its preferred position,
    // measure it against the viewport and, ONLY if it overflows, flip to the
    // side with more room (vertical) and/or clamp horizontally, capping height
    // to the available space. Runs at most once per show (correctedRef), so a
    // tooltip that already fits keeps its preferred placement untouched.
    useLayoutEffect(() => {
        if (!pos || correctedRef.current) return;
        const popEl = popRef.current;
        const trig = triggerRect();
        if (!popEl || !trig) return;
        correctedRef.current = true;
        const pop = popEl.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const M = 8;
        const fix = {};
        if (pop.top < M || pop.bottom > vh - M) {
            const roomAbove = trig.top - M;
            const roomBelow = vh - trig.bottom - M;
            if (roomBelow >= roomAbove) {
                fix.top = `${trig.bottom + 4}px`;
                fix.transform = "none";
                fix.maxHeight = Math.max(80, Math.floor(roomBelow));
            } else {
                fix.top = `${trig.top - 6}px`;
                fix.transform = "translateY(-100%)";
                fix.maxHeight = Math.max(80, Math.floor(roomAbove));
            }
        }
        if (pop.right > vw - M) { fix.left = undefined; fix.right = `${M}px`; }
        else if (pop.left < M) { fix.right = undefined; fix.left = `${M}px`; }
        if (Object.keys(fix).length) setPos((p) => ({ ...p, ...fix }));
    }, [pos, triggerRect]);

    useLayoutEffect(() => {
        if (!anchorRect) return;
        show();
    }, [anchorRect, show]);

    const child = children ? cloneElement(children, {
        ref: (node) => {
            ref.current = node;
            const r = children.props.ref;
            if (typeof r === "function") r(node);
            else if (r && typeof r === "object") r.current = node;
        },
        onMouseEnter: (e) => { show(); children.props.onMouseEnter?.(e); },
        onMouseLeave: (e) => { hide(); children.props.onMouseLeave?.(e); },
        onFocus: (e) => { show(); children.props.onFocus?.(e); },
        onBlur: (e) => { hide(); children.props.onBlur?.(e); },
    }) : null;

    return (
        <>
            {child}
            {pos ? createPortal(
                <div
                    ref={popRef}
                    role="tooltip"
                    className={[
                        `pointer-events-none fixed ${TOOLTIP_LAYER} border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--ink)] shadow-lg`,
                        wide ? "whitespace-normal" : "whitespace-nowrap",
                        wide && !widePx ? "max-w-[300px]" : "",
                    ].join(" ")}
                    style={{
                        top: pos.top,
                        ...(pos.right != null ? { right: pos.right } : { left: pos.left }),
                        transform: pos.transform,
                        ...(widePx != null ? { maxWidth: widePx } : null),
                        ...(pos.maxHeight != null ? { maxHeight: pos.maxHeight, overflow: "hidden" } : null),
                    }}
                >
                    {label}
                </div>,
                document.body,
            ) : null}
        </>
    );
}
